/**
 * carePlanBlockHelp.js — Ajuda contextual por bloco do Plano de Cuidados.
 *
 * Fonte de verdade da metadata dos 28 blocos, usada tanto por:
 *   - `CarePlanBlocksEditor` → popover "?" ao lado do título do bloco
 *   - `CarePlanGuide` → cartões descritivos na página /help/care-plan-guide
 *
 * Cada entrada tem 3 chaves obrigatórias (`quando`, `como`, `exemplo`) — se
 * um bloco não estiver aqui, o popover mostra apenas um link para o guião.
 */

export const BLOCK_HELP = {
  // ── Identificação & Contexto ──────────────────────────────────────────
  service_identification: {
    quando: "Sempre — auto-preenchido a partir do lead.",
    como: "Confirma dados demográficos: nome, idade, endereço, doenças crónicas relevantes, contactos de emergência.",
    exemplo: "D. Maria Silva, 84 anos, viúva, Alzheimer em fase moderada, tem filha em Lisboa (número operacional).",
  },
  critical_summary: {
    quando: "Antes de qualquer AF visitar.",
    como: "Em 3-5 linhas: o que a AF PRECISA de saber nos primeiros 60 segundos. Riscos imediatos, alergias, tabus.",
    exemplo: "Diabética insulino-dependente. Não pode comer açúcar. Nunca falar do marido falecido (chora). Se cair, chamar 112 antes de tudo.",
  },

  // ── A Pessoa ──────────────────────────────────────────────────────────
  person_profile: {
    quando: "Ao construir a versão inicial.",
    como: "Lista de traços: profissão anterior, gostos, medos, temas favoritos, o que a torna feliz. NÃO é o clínico.",
    exemplo: "Ex-professora primária. Adora música clássica. Detesta televisão à noite. Fica ansiosa se lhe mexem na mala.",
  },
  functional_assessment: {
    quando: "Após RT com a família.",
    como: "Nível de autonomia em cada actividade (higiene, alimentação, mobilidade, cognição). Usa a escala 'autónomo / supervisão / apoio parcial / apoio total'.",
    exemplo: "Higiene: apoio parcial (precisa de ajuda a entrar/sair do chuveiro). Mobilidade: autónomo com bengala. Cognição: reconhece cuidadores há +2 meses; confunde nomes de familiares após 15h.",
  },

  // ── Plano de Cuidados propriamente dito ───────────────────────────────
  objectives: {
    quando: "Sempre.",
    como: "Objectivos gerais (não SMART) do plano. Frases curtas, uma por objectivo.",
    exemplo: "Manter autonomia na mobilidade · Preservar rotina alimentar · Reduzir risco de queda.",
  },
  priorities: {
    quando: "Sempre.",
    como: "Ordem de prioridade dos objectivos. Uma linha por prioridade.",
    exemplo: "1. Prevenir queda · 2. Adesão à medicação · 3. Estimular cognição.",
  },
  needs_objectives: {
    quando: "Sempre — é o coração do plano.",
    como: "Para cada necessidade da RT: Necessidade → Objectivo mensurável → Intervenção concreta → Frequência.",
    exemplo: "Mobilidade reduzida → manter marcha com bengala → caminhada assistida 15 min/dia → diária.",
  },
  recommendations: {
    quando: "Se houver ajustes ao ambiente ou rotina.",
    como: "Uma linha por recomendação. Focar em modificações concretas do ambiente ou rotinas.",
    exemplo: "Instalar tapete anti-derrapante no chuveiro · Comprar organizador semanal de medicação · Retirar carpetes soltas do corredor.",
  },
  routines: {
    quando: "Sempre.",
    como: "Rotina diária hora-a-hora. Cada linha: hora + actividade + instruções específicas.",
    exemplo: "09:00 · Preparar pequeno-almoço · Servir na sala (ela prefere), chá com leite + torrada.",
  },
  weekly_plan: {
    quando: "Após definir objectivos.",
    como: "Tarefa por linha, marcar dias em que é executada (Seg-Dom). É a base do horário da AF.",
    exemplo: "Ginástica leve × Seg/Qua/Sex · Banho assistido × Ter/Sáb · Passeio jardim × Diário.",
  },
  nutrition_hydration: {
    quando: "Se houver restrições ou dieta específica.",
    como: "Restrições absolutas, preferências, líquidos/dia, texturas se disfagia. Nunca prescrever suplementos.",
    exemplo: "Diabética, dieta hipoglicémica prescrita pela nutricionista. 1.5L água/dia. Cortar sempre carne (não engole bem).",
  },
  medication_notes: {
    quando: "Se toma medicação regular (nota rápida — para versão formal usar 'Medicação (nível clínico)').",
    como: "Só notas operacionais: quem prepara, onde está guardada, quando dar. Sem doses.",
    exemplo: "Caixa semanal preparada pela filha aos sábados. Guardada na cozinha (armário superior). Dar sempre com o pequeno-almoço.",
  },
  medication_level: {
    quando: "Se toma medicação regular.",
    como: "Só nomes genéricos + horário + observações. NUNCA doses (competência médica). Alergias em MAIÚSCULAS.",
    exemplo: "Metformina — manhã e noite após refeição. Alergia PENICILINA. Ver caixa semanal preparada pela filha ao sábado.",
  },

  // ── Riscos, comunicação e follow-up ───────────────────────────────────
  risks: {
    quando: "Sempre. Sem excepção.",
    como: "Cada risco: descrição + probabilidade (baixa/média/alta) + acção preventiva + resposta se acontecer.",
    exemplo: "Queda no chuveiro (alta): tapete anti-derrapante + AF entra sempre antes. Se cair: verificar consciência → 112 → filha.",
  },
  communication_escalation: {
    quando: "Sempre.",
    como: "Quem chama a quem, quando, por que canal. Formato: 'Situação → Contactar X (telefone Y) → Timing'.",
    exemplo: "Febre >38.5°C → Filha Ana (9XX XXX XXX) imediato + Gestor no ClickUp em 30 min. Recusa comer 2 refeições → só Gestor.",
  },
  followup_plan: {
    quando: "Sempre.",
    como: "Ritmo de revisão: quando o Gestor liga à família, quando o TSC visita, quando se refaz o plano.",
    exemplo: "Semana 1-4: Gestor liga sexta 16h. TSC visita ao 15º dia. Reavaliação do plano ao 90º dia.",
  },
  contacts: {
    quando: "Sempre.",
    como: "Médico de família, farmácia, familiar-chave, enfermeiro. Rótulo + nome + telefone + notas ('só antes das 20h').",
    exemplo: "Dr. Rui Alves (Médico Fam.) — 217 XXX XXX — só terças e quintas manhã.",
  },
  activities: {
    quando: "Se houver acções pontuais estruturadas.",
    como: "Categoria (higiene/refeições/etc) + acção concreta + frequência + responsável.",
    exemplo: "Refeições · Preparar sopa de legumes · diária · AF.",
  },

  // ── Notas confidenciais ──────────────────────────────────────────────
  internal_notes: {
    quando: "Para tudo o que só a Caring vê.",
    como: "Situações familiares delicadas, conflitos, observações que ajudam o Gestor mas nunca deveriam chegar ao cliente.",
    exemplo: "Filha e filho discutem sobre pagamento — endereçar comunicações sempre à filha. Cliente esconde declínio cognitivo à família.",
  },
  family_notes: {
    quando: "Notas partilhadas com a família.",
    como: "Curto, tom institucional. O que a família precisa de saber que não está nos outros blocos públicos.",
    exemplo: "A Caring recomenda revisão da medicação com o médico de família até final do mês.",
  },
  caregiver_profile: {
    quando: "Perfil do cuidador ideal para este cliente.",
    como: "Características que ajudam o match: idade preferida, sexo, competências, línguas, personalidade.",
    exemplo: "Idade 35-55. Preferência por mulher. Paciência para explicar duas vezes. Ligado a música clássica é bónus.",
  },

  // ── Vista AF (Ajudante Familiar) ──────────────────────────────────────
  caregiver_before_start: {
    quando: "Sempre. É a primeira secção que a AF lê.",
    como: "5-8 linhas com o essencial: como tratar a pessoa, alergias, tabus, o que fazer nos primeiros 15 min.",
    exemplo: "Tratar por D. Maria (nunca 'senhora'). Não falar de política nem de morte. Ao chegar, sentar-se ao lado dela 5 min antes de qualquer tarefa.",
  },
  caregiver_tratar_por: {
    quando: "Sempre.",
    como: "Como a pessoa se identifica. Curto — 3-5 palavras.",
    exemplo: "D. Maria",
  },
  caregiver_mobility_summary: {
    quando: "Se houver especificidades de mobilidade.",
    como: "Resumo em 1-2 linhas para a AF ter presente antes de qualquer transferência.",
    exemplo: "Anda com bengala. Cansa-se após 15 min de marcha. Precisa de apoio ao levantar do sofá.",
  },
  caregiver_communication_summary: {
    quando: "Se houver dificuldade de comunicação.",
    como: "Como a AF deve comunicar: volume, tempo de espera, palavras a evitar, sinais não-verbais.",
    exemplo: "Ouvido esquerdo débil — falar do lado direito. Dar 10s de espera após pergunta. Evitar 'sim ou não' — perguntas abertas curtas.",
  },
  caregiver_preference_summary: {
    quando: "Se houver preferências fortes.",
    como: "Preferências que a AF deve respeitar sempre.",
    exemplo: "Prefere sopa antes do prato principal. Recusa banho antes do café. Adora música de fados enquanto arruma.",
  },
  caregiver_person_desc: {
    quando: "Sempre.",
    como: "Um parágrafo narrativo, humano. Origem, personalidade, temas seguros de conversa.",
    exemplo: "A D. Maria foi professora primária durante 40 anos em Coimbra. Gosta de música clássica e de recordar aulas. Falar de flores é sempre bem-vindo, ela cultivou-as toda a vida.",
  },
  caregiver_daily_tasks: {
    quando: "Sempre.",
    como: "Grelha estruturada: acção + horário + como fazer. É o roteiro passo-a-passo da AF.",
    exemplo: "09:00 · Preparar pequeno-almoço (chá com leite + torrada) · Servir na sala, não na cozinha (ela prefere).",
  },
  care_delivery: {
    quando: "Se houver técnica específica.",
    como: "Como fazer a higiene, transferências, alimentação assistida. Passos concretos e imagens mentais.",
    exemplo: "Banho: colocar cadeira de banho, encher chuveiro, chamá-la, esperar 30s antes de retirar roupa (dá-lhe controlo).",
  },
  caregiver_before_ending: {
    quando: "Sempre.",
    como: "Checklist de 4-6 itens que a AF cumpre nos últimos 15 min: registar, arrumar, deixar bilhete, contactar Gestor se relevante.",
    exemplo: "1. Preencher app · 2. Deixar copo de água ao lado da cama · 3. Ligar luz de presença · 4. Enviar WhatsApp ao Gestor com resumo do dia.",
  },
  caregiver_when_to_contact: {
    quando: "Sempre.",
    como: "Lista clara de gatilhos. A AF não deve ficar em dúvida sobre se pode/deve ligar.",
    exemplo: "Ligar imediatamente se: febre >38, queda, recusa comer 2 refeições, choro persistente, familiar aparece agitado.",
  },

  // ── Vista Cliente / Família ──────────────────────────────────────────
  client_summary_intro: {
    quando: "Sempre.",
    como: "2-3 parágrafos que o cliente lê primeiro. Foco em segurança, dignidade e continuidade — não em diagnóstico.",
    exemplo: "A Caring propõe acompanhar a D. Maria com um plano centrado na sua dignidade e conforto. Priorizamos manter a sua rotina, respeitar as suas preferências e garantir uma equipa estável.",
  },
  client_special_attention: {
    quando: "Se houver algo além do standard.",
    como: "Item + porquê + como a Caring vai gerir. É o compromisso público.",
    exemplo: "Alergia à penicilina — todos os elementos da equipa foram informados; caixa de medicação tem etiqueta vermelha.",
  },
  client_team_members: {
    quando: "Após atribuição da equipa.",
    como: "Nome, função, foto (opcional). Só quem realmente vai ao domicílio. Se muda, gerar nova versão.",
    exemplo: "Ana (AF principal), Beatriz (backup), Gestora Daniela.",
  },
  client_preferences_kv: {
    quando: "Sempre.",
    como: "Preferências relevantes: refeições, banho, temas, música, horários. O que a família confirma que a Caring vai respeitar.",
    exemplo: "Refeições: sopa sempre ao almoço · Banho: manhã · Música: fados e Amália · Sesta: 14h-15h.",
  },
  client_followup_stages: {
    quando: "Sempre.",
    como: "Timeline pública: quando o Gestor contacta, quando o TSC visita, quando o plano é reavaliado. Dá previsibilidade.",
    exemplo: "Dia 15: visita do TSC. Dia 30: chamada da Gestora à família. Dia 90: reavaliação do plano.",
  },
  client_followup_intro: {
    quando: "Antes da tabela de etapas.",
    como: "1-2 linhas a contextualizar o acompanhamento.",
    exemplo: "A Caring acompanha a evolução do serviço com marcos claros nas primeiras 12 semanas para garantir qualidade e ajuste contínuo.",
  },
};
